import cloneDeep from 'lodash/cloneDeep'
import { defineStore } from 'pinia'
import type { V2WorkflowCommandResponse } from 'src/store/types/V2WorkflowCommandResponse'
import type { AnnotationMetadata } from '@/backend/darwin/loadPaginatedAnnotationMetadata'
import type { ComputedRef } from 'vue'
import { triggerRef, computed, ref, shallowRef } from 'vue'

import { useClassesById } from '@/modules/Classes/useClassesById'
import { isVideoRasterLayerAnnotation } from '@/composables/useEditorV2/useConnectStore/resolveRasterMapping'
import { useSharedEditorLayout } from '@/composables/useEditorV2/useLayout'
import { useShouldPaginateAnnotations } from '@/composables/useShouldPaginateAnnotations'
import { useStageSpecificClasses } from '@/composables/useStageSpecificClasses'
import type { PartialRecord } from '@/core/helperTypes'
import type { Range } from '@/modules/Editor/types'
import type { VideoRaster } from '@/modules/Editor/models/raster/VideoRaster'
import type { UpdateMeta, DuplicateMeta } from '@/modules/Editor/serialization/types'
import { useSharedAnnotationRequestCounter } from '@/modules/Workview/useAnnotationRequestCounter'
import { useDatasetItemsStore } from '@/modules/Datasets/useDatasetItemsStore'
import { useWorkflowInWorkviewStore } from '@/modules/Workview/useWorkflowInWorkviewStore'
import type { PartialVideoDataRequestPayload } from '@/store/types'
import { LoadingStatus } from '@/store/types'
import { isAutotrackFrame } from '@/modules/Editor/utils/autotrackUtils'
import type { GenerateInstanceIdResponse } from '@/store/types/GenerateInstanceIdResponse'
import type {
  PaginatedAnnotationPayload,
  V2AnnotationPayload,
  MetadataIndicesFields,
} from '@/store/types/StageAnnotationPayload'
import type {
  UpdateAnnotationCommand,
  UpdateSequenceAnnotationCommand,
  ReorderAnnotationCommand,
} from '@/store/types/V2WorkflowCommandPayload'
import { copyV2Annotations } from '@/backend/darwin/copyV2Annotations'
import type { Params as CopyStoreAnnotationsParams } from '@/backend/darwin/copyV2Annotations'
import { generateInstanceId } from '@/backend/darwin/generateInstanceId'
import { loadV2Annotations } from '@/backend/darwin/loadV2Annotations'
import { loadV2AnnotationsSnapshot } from '@/backend/darwin/loadV2AnnotationsSnapshot'
import { sendV2Commands } from '@/backend/darwin/sendV2Commands'
import { constructError } from '@/backend/error'
import type { ParsedError } from '@/backend/error'
import { ErrorCodes } from '@/backend/error/errors'
import type { ErrorWithMessage, ParsedValidationError } from '@/backend/error/types'
import { MainAnnotationType } from '@/core/annotationTypes'
import { getMainAnnotationType } from '@/core/annotationTypes'

import { useReactiveRTree } from '@/pinia/utils/useReactiveRTree'
import { isVideoAnnotationDataPayload } from '@/modules/Editor/models/annotation/annotationKindValidator'
import { hasAnnotationPropertiesData } from '@/modules/Workview/hasAnnotationPropertiesData'
import { useWorkviewSettingsStore } from '@/pinia/useWorkviewSettingsStore'
import { computedWithControl } from '@vueuse/core'
import { setAnnotationsForCurrentFrameIndex } from '@/composables/useEditorV2/useConnectStore/setAnnotationsForCurrentFrameIndex'
import { useReactiveMapLinkedList } from '@/pinia/utils/useReactiveMapLinkedList'
import { GET_RESPONSE_WITH_ANNOTATIONS } from '@/performance/keys'
import { useFeatureFlagsStore } from '@/pinia/useFeatureFlagsStore'
import { mark } from '@/performance/mark'
import { willApplyPropertyCorrection } from '@/modules/Workview/willApplyPropertyCorrection'
import type { View } from '@/modules/Editor/views/view'
import { isViewsAnnotation } from '@/modules/Workview/isViewsAnnotation'
import { useCurrentItemStore } from '@/modules/Workview/useCurrentItemStore'
import { useAnnotationPackageManager } from '@/pinia/utils/AnnotationPackage/useAnnotationPackageManager'
import type { FramesRange } from '@/uiKitLegacy/FramesRange'
import type { SequenceData } from '@/core/annotations'
import { useDatasetStore } from '@/modules/Datasets/useDatasetStore'
import { getAnnotationRange } from '@/core/utils/frames'

type RTreeAnnotation = Range & {
  id: string
}

export type UseV2AnnotationsError = ErrorWithMessage | ParsedValidationError

export type CreateAnnotationResponse =
  | { data: V2AnnotationPayload }
  | { error: UseV2AnnotationsError }
export type UpdateAnnotationResponse =
  | { data: V2WorkflowCommandResponse }
  | { error: UseV2AnnotationsError }
export type DeleteAnnotationResponse = { data: string } | { error: UseV2AnnotationsError }
export type DeleteAnnotationsResponse = { data: string[] } | { error: UseV2AnnotationsError }
export type getInstanceIdResponse =
  | { data: GenerateInstanceIdResponse }
  | { error: UseV2AnnotationsError }

class CreateActionError extends Error {
  constructor(message: string) {
    super(`annotationsV2Store.create_annotation: ${message}`)
  }
}

class UpdateActionError extends Error {
  constructor(message: string) {
    super(`annotationsV2Store.update_annotation: ${message}`)
  }
}

class ReorderActionError extends Error {
  constructor(message: string) {
    super(`annotationsV2Store.reorder_annotation: ${message}`)
  }
}

class DeleteActionError extends Error {
  constructor(message: string) {
    super(`annotationsV2Store.delete_annotation: ${message}`)
  }
}

export enum ACTIONS {
  GET_ANNOTATION = 'getAnnotation',
  GET_ANNOTATION_MAIN_TYPE = 'getAnnotationMainType',
  GET_ALL_ANNOTATIONS_BY_ANNOTATION_CLASS_ID = 'getAllAnnotationsByAnnotationClassId',
  GET_ALL_ANNOTATIONS_BY_AUTHOR_ID = 'getAllAnnotationsByAuthorId',
  REORDER_ANNOTATION = 'reorderAnnotation',
  MOVE_BOTTOM = 'moveBottom',
  MOVE_TOP = 'moveTop',
  CLEAR_ANNOTATIONS = 'clearAnnotations',
  SET_ANNOTATION = 'setAnnotation',
  SET_ANNOTATIONS = 'setAnnotations',
  LOAD_ANNOTATIONS = 'loadAnnotations',
  COPY_ANNOTATIONS = 'copyAnnotations',
  SET_ANNOTATION_READONLY = 'setAnnotationReadOnly',
  DELETE_ANNOTATION = 'deleteAnnotation',
  DELETE_ANNOTATIONS = 'deleteAnnotations',
  UPDATE_ANNOTATION = 'updateAnnotation',
  CREATE_ANNOTATION = 'createAnnotation',
  DUPLICATE_ANNOTATION = 'duplicateAnnotation',
  LOAD_ANNOTATION_SNAPSHOT = 'loadAnnotationsSnapshot',
  GET_INSTANCE_ID = 'getInstanceId',
  UPDATE_VIDEO_RASTER_LAYER_ANNOTATION = 'updateVideoRasterLayerAnnotation',
}

export type StoreAnnotationWithItemId = {
  storeAnnotation: Omit<V2AnnotationPayload, 'actors' | 'annotation_group_id'>
  itemId: string
  updateMeta?: UpdateMeta
}

const ANNOTATIONS_SIZE_LIMIT_BYTES = 10_000_000

type ZIndexed = { z_index: number | null }

/**
 * Should be used as the single sorting method for annotations.
 *
 * If the sort order needs to be changed, we should go with either extending the method with
 * a sort direction argument, or reversing the result.
 *
 * Sorts in descending order, nulls first
 */
const compareByZIndex = (a: ZIndexed, b: ZIndexed): number => {
  if (a.z_index === b.z_index) {
    return 0
  }
  if (a.z_index === null) {
    return -1
  }
  if (b.z_index === null) {
    return 1
  }
  return a.z_index < b.z_index ? 1 : -1
}

/**
 * Handles all CRUD operations related to annotations.
 */
export const useV2AnnotationsStore = defineStore('annotationsV2', () => {
  const itemsStore = useDatasetItemsStore()
  const sharedLayout = useSharedEditorLayout()
  const currentItemStore = useCurrentItemStore()
  const { allowedClassIds } = useStageSpecificClasses()
  const { classesById, editorClassesById } = useClassesById()
  const featuresStore = useFeatureFlagsStore()
  const workflowInWorkviewStore = useWorkflowInWorkviewStore()
  const workviewSettings = useWorkviewSettingsStore()

  const _framesNeedsLoading = shallowRef(new Set<number>())
  const loadedFrames = shallowRef(new Set<number>())

  const metadataIsLoaded = ref(false)

  const annotationPackageManager = useAnnotationPackageManager()

  const framesNeedsLoading = computed(() =>
    featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.framesNeedsLoading.value
      : _framesNeedsLoading.value,
  )

  /**
   * Keeps the map of the tag annotations
   */
  const tagAnnotationsMap = useReactiveMapLinkedList<V2AnnotationPayload & MetadataIndicesFields>()

  /**
   * Keeps the tag ids using r-tree data structure
   * we use it mainly for the fast search of the tags by the
   * frame indexes range - [minX, maxX]
   * and segment indexes range - [minY, maxY] (we convert slot names into indexes)
   */
  const tagsRTree = useReactiveRTree<RTreeAnnotation>()

  /**
   * Keeps the sorted map of the non tag annotations
   *
   * We use this data structure to provide the fast re-order of the annotations
   */
  const annotationsMap = useReactiveMapLinkedList<V2AnnotationPayload & MetadataIndicesFields>()
  /**
   * Keeps the annotation ids using r-tree data structure
   * we use it mainly for the fast search of the annotations by the
   * frame indexes range - [minX, maxX]
   * and segment indexes range - [minY, maxY] (we convert slot names into indexes)
   */
  const annotationsRTree = useReactiveRTree<RTreeAnnotation>()

  /**
   * Keeps the masks annotations map
   */
  const masksMap = useReactiveMapLinkedList<V2AnnotationPayload>()

  /**
   * Keeps the single raster layer annotation
   */
  const rasterLayersMap = useReactiveMapLinkedList<V2AnnotationPayload>()

  const allTags = tagAnnotationsMap.values
  const allAnnotations = annotationsMap.values
  const allMasks = masksMap.values
  const allRasterLayers = rasterLayersMap.values

  const currentSlotName = computed(() => workviewSettings.activeSlotName)

  /**
   * Calculated map of slot names to slot indexes
   * We calculate it once for current item
   * and use it as Y axis for the r-tree
   */
  const itemSlotIndexMap = computed(() => {
    const res: PartialRecord<string, number> = {}

    itemsStore.currentItem?.slots.forEach((slot, index) => {
      res[slot.slot_name] = index
    })

    return res
  })

  const getAnnotation = (id: string): (V2AnnotationPayload & MetadataIndicesFields) | null => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      return annotationPackageManager.getAnnotation(id)
    }

    return (
      annotationsMap.get(id) ||
      tagAnnotationsMap.get(id) ||
      masksMap.get(id) ||
      rasterLayersMap.get(id)
    )
  }

  const getAnnotationRef = (
    id: string,
  ): ComputedRef<(V2AnnotationPayload & MetadataIndicesFields) | null> => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      return annotationPackageManager.getAnnotationRef(id)
    }

    const annRef = annotationsMap.getRef(id)
    const tagRef = tagAnnotationsMap.getRef(id)
    const maskRef = masksMap.getRef(id)

    return computed(() => annRef.value || tagRef.value || maskRef.value)
  }

  /**
   * Resolve the annotation main type by class id
   */
  const getAnnotationMainTypeByClassId = (annotationClassId: number): MainAnnotationType | null => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      return annotationPackageManager.getAnnotationMainTypeByClassId(annotationClassId)
    }

    const annotationClassPayload = classesById.value[annotationClassId]
    if (!annotationClassPayload) {
      return null
    }

    return getMainAnnotationType(annotationClassPayload.annotation_types) || null
  }

  /**
   * Resolve the annotation main type by id
   */
  const getAnnotationMainTypeById = (id: string): MainAnnotationType | null => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      return annotationPackageManager.getAnnotationMainTypeById(id)
    }

    const annotationClassId = getAnnotation(id)?.annotation_class_id
    if (!annotationClassId) {
      throw new Error(
        'trying to access the class id of an annotation which is not laoded within the editor',
      )
    }

    return getAnnotationMainTypeByClassId(annotationClassId)
  }

  const isRasterLayer = (classId: number): boolean =>
    getAnnotationMainTypeByClassId(classId) === MainAnnotationType.RasterLayer

  const isAnnotationMask = (classId: number): boolean =>
    getAnnotationMainTypeByClassId(classId) === MainAnnotationType.Mask

  const isAnnotationTag = (classId: number): boolean => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      return annotationPackageManager.isAnnotationTag(classId)
    }

    return getAnnotationMainTypeByClassId(classId) === MainAnnotationType.Tag
  }

  const getRangeFromAnnotation = (
    ann: Pick<V2AnnotationPayload, 'id' | 'annotation_class_id' | 'data' | 'context_keys'>,
  ): RTreeAnnotation => {
    const segments = 'segments' in ann.data ? ann.data.segments : undefined
    const isImageAnnotation = segments === undefined
    const isMaskAnnotation = isAnnotationMask(ann.annotation_class_id)

    // always return the full range for masks or image annotations
    if (isImageAnnotation || isMaskAnnotation) {
      return {
        minX: 0,
        maxX: currentItemStore.activeSlotFramesCount,
        minY: itemSlotIndexMap.value[ann.context_keys?.slot_names?.[0] || 0] ?? 0,
        maxY: itemSlotIndexMap.value[ann.context_keys?.slot_names?.[0] || 0] ?? 0,
        id: ann.id,
      }
    }

    const startFrame = segments?.[0][0] || 0
    const endFrame =
      segments?.[segments.length - 1][1] || 0 || currentItemStore.activeSlotFramesCount
    return {
      minX: startFrame,
      maxX: endFrame - 1, // needs to be 1 less than the usual because of RTree calcs
      minY: itemSlotIndexMap.value[ann.context_keys?.slot_names?.[0] || 0] ?? 0,
      maxY: itemSlotIndexMap.value[ann.context_keys?.slot_names?.[0] || 0] ?? 0,
      id: ann.id,
    }
  }

  // Keep the list of all annotations, tags and masks for INTERNAL use
  const annotationsEntries = computed(() => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      return annotationPackageManager.annotationsEntries.value
    }
    return [...allAnnotations.value, ...allTags.value, ...allMasks.value]
  })

  const getAllIds = (
    range: [number, number],
    slotIndexRange: [number, number],
  ): { id: string }[] => {
    const tags = tagsRTree.getIds(range, slotIndexRange)
    const annotations = annotationsRTree.getIds(range, slotIndexRange)
    const slotName = Object.keys(itemSlotIndexMap.value)[slotIndexRange[0]]

    return [
      // Make sure to always show tags on top of the RHS Layer Bar
      ...tags,
      ...annotations.sort((a, b) => {
        const aAnnIndex = annotationsMap.indexOf(a.id)
        const bAnnIndex = annotationsMap.indexOf(b.id)
        if (aAnnIndex === -1 || bAnnIndex === -1) {
          return 0
        }

        return compareByZIndex({ z_index: bAnnIndex }, { z_index: aAnnIndex })
      }),
      ...allMasks.value.filter((mask) => isViewsAnnotation(slotName, mask)),
    ]
  }

  const currentFrameAllAnnotationsPerSlot = computedWithControl(
    () => [workviewSettings.multiSlotFrameIndexes, annotationsEntries.value],
    () => {
      const res: PartialRecord<string, { id: string }[]> = {}

      Object.entries(workviewSettings.multiSlotFrameIndexes).forEach(([slotName, frameIndex]) => {
        if (!slotName || frameIndex === undefined) {
          throw new Error('Invalid slot name or index')
        }

        const slotIndex = itemSlotIndexMap.value[slotName] || 0
        res[slotName] = getAllIds([frameIndex, frameIndex], [slotIndex, slotIndex])
      })

      return res
    },
  )

  /*
   * Returns a list of annotations (including tags) grouped by slot name
   */
  const currentFrameInViewAnnotationsPerSlot = computedWithControl(
    () => [workviewSettings.multiSlotFrameIndexes, annotationsEntries.value],
    () => {
      const res: PartialRecord<string, { id: string }[]> = {}

      Object.entries(workviewSettings.multiSlotFrameIndexes).forEach(([slotName, index]) => {
        if (!slotName || index === undefined) {
          throw new Error('Invalid slot name or index')
        }

        res[slotName] = (currentFrameAllAnnotationsPerSlot.value[slotName] || []).filter(
          ({ id }) => {
            const annotation =
              annotationsMap.get(id) || tagAnnotationsMap.get(id) || masksMap.get(id)

            // invalid annotation
            if (!annotation?.data) {
              return false
            }

            return true
          },
        )
      })

      return res
    },
  )

  const authorAnnotationIdsMap = computed<PartialRecord<number, string[]>>(() => {
    const map: PartialRecord<number, string[]> = {}

    annotationsEntries.value.forEach((ann) => {
      ann.actors.forEach((author) => {
        if (!map[author.user_id]) {
          map[author.user_id] = []
        }

        map[author.user_id]?.push(ann.id)
      })
    })

    return map
  })

  const classAnnotationIdsMap = computed<PartialRecord<number, string[]>>(() => {
    const map: PartialRecord<number, string[]> = {}

    annotationsEntries.value.forEach((ann) => {
      if (!map[ann.annotation_class_id]) {
        map[ann.annotation_class_id] = []
      }

      map[ann.annotation_class_id]?.push(ann.id)
    })

    return map
  })

  const getAllAnnotationsByAuthorId = (authorId: number): V2AnnotationPayload[] =>
    annotationsEntries.value.filter(({ actors }) => actors.find((a) => a.user_id === authorId))

  const getAllAnnotationsByAnnotationClassId = (annotationClassId: number): V2AnnotationPayload[] =>
    annotationsEntries.value.filter(
      ({ annotation_class_id }) => annotation_class_id === annotationClassId,
    )

  const annotationsWithProperties = ref<PartialRecord<string, boolean>>({})
  const hasAnnotationsWithProperties = computed(
    () => Object.keys(annotationsWithProperties.value).length > 0,
  )

  /**
   * Track if any annotation has properties data
   * We show toast on stage change based on this flag.
   * Properties are not supported in time travel mode.
   */
  const recordAnnotationProperties = (annId: string): void => {
    const ann = getAnnotation(annId)

    if (!ann || !hasAnnotationPropertiesData(ann)) {
      delete annotationsWithProperties.value[annId]
      return
    }

    annotationsWithProperties.value[ann.id] = true
  }

  /**
   * Apply updates to the MetadataIndicesFields extra fields
   * NOTE: To perform the annotation update we're working with `frames` and `sub_frames` fields
   * and we need to update the indices in the metadata fields
   *
   * The function purpose is to provide optimistic UI updates for the annotation keyframes.
   */
  const updateMetadataIndices = (
    ann: V2AnnotationPayload & MetadataIndicesFields,
    meta?: UpdateMeta & DuplicateMeta,
  ): V2AnnotationPayload & MetadataIndicesFields => {
    if (!isVideoAnnotationDataPayload(ann.data)) {
      return ann
    }

    const frames_indices = ann.frames_indices || new Set<number>()
    const sub_frames_indices = ann.sub_frames_indices || new Set<number>()
    const auto_track_frames_indices = ann.auto_track_frames_indices || new Set<number>()

    // No updateMeta info provided, we need to get all indexes from the annotation data
    if (!meta || meta.hasChangedLength) {
      Object.keys(ann.data.frames).forEach((i) => {
        if (
          (isVideoAnnotationDataPayload(ann.data) && isAutotrackFrame(ann.data.frames[i])) ||
          auto_track_frames_indices.has(+i)
        ) {
          auto_track_frames_indices.add(+i)
          return
        }

        auto_track_frames_indices.delete(+i)
        frames_indices.add(+i)
      })
      Object.keys(ann.data.sub_frames).map((i) => {
        sub_frames_indices.add(+i)
      })
    }

    // Update frames indices using updateMeta info
    if (meta?.updatedFramesIndices) {
      meta.updatedFramesIndices.forEach((i) => {
        if (!isVideoAnnotationDataPayload(ann.data)) {
          return
        }

        if (!ann.data.frames[i]) {
          frames_indices.delete(i)
          auto_track_frames_indices.delete(i)
          return
        }

        if (isAutotrackFrame(ann.data.frames[i])) {
          auto_track_frames_indices.add(i)
          return
        }

        auto_track_frames_indices.delete(i)
        frames_indices.add(i)
      })
    }

    // Update sub frames indices using updateMeta info
    if (meta?.updatedSubFramesIndices) {
      meta.updatedSubFramesIndices.forEach((i) => {
        if (!isVideoAnnotationDataPayload(ann.data)) {
          return
        }

        if (!ann.data.sub_frames[i]) {
          sub_frames_indices.delete(i)
          return
        }

        sub_frames_indices.add(i)
      })
    }

    if (meta?.segmentShiftAmount) {
      const { segmentShiftAmount } = meta
      const newFramesIndices = new Set<number>()
      const newSubFramesIndices = new Set<number>()
      const newAutoTrackFramesIndices = new Set<number>()

      frames_indices.forEach((i) => {
        newFramesIndices.add(i + segmentShiftAmount)
      })

      auto_track_frames_indices.forEach((i) => {
        newAutoTrackFramesIndices.add(i + segmentShiftAmount)
      })

      sub_frames_indices.forEach((i) => {
        newSubFramesIndices.add(i + segmentShiftAmount)
      })

      frames_indices.clear()
      sub_frames_indices.clear()
      auto_track_frames_indices.clear()

      newFramesIndices.forEach((i) => frames_indices.add(i))
      newSubFramesIndices.forEach((i) => sub_frames_indices.add(i))
      newAutoTrackFramesIndices.forEach((i) => auto_track_frames_indices.add(i))
    }

    if (meta?.firstKeyFrameOnly) {
      frames_indices.clear()
      sub_frames_indices.clear()
      auto_track_frames_indices.clear()
    }

    return {
      ...ann,
      frames_indices,
      sub_frames_indices,
      auto_track_frames_indices,
    }
  }

  /**
   * Set a single annotation in the store. If the annotation already exists,
   * it will be overwritten.
   */
  const setAnnotation = (ann: V2AnnotationPayload): void => {
    const updatedAnn = featuresStore.featureFlags.ANNOTATIONS_PAGINATION
      ? updateMetadataIndices({
          ...ann,
          annotation_class_id: ann.annotation_class_id,
          data: ann.data,
          id: ann.id,
        })
      : {
          ...ann,
          annotation_class_id: ann.annotation_class_id,
          data: ann.data,
          id: ann.id,
        }

    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.updateAnnotation(updatedAnn)

      recordAnnotationProperties(updatedAnn.id)
      return
    }

    if (isRasterLayer(updatedAnn.annotation_class_id)) {
      rasterLayersMap.set(updatedAnn.id, updatedAnn)
      return
    }

    if (isAnnotationMask(updatedAnn.annotation_class_id)) {
      masksMap.set(updatedAnn.id, updatedAnn)
      recordAnnotationProperties(updatedAnn.id)
      return
    }

    if (isAnnotationTag(updatedAnn.annotation_class_id)) {
      tagAnnotationsMap.set(updatedAnn.id, updatedAnn)
      tagsRTree.setItem(getRangeFromAnnotation(updatedAnn))
      recordAnnotationProperties(updatedAnn.id)

      return
    }

    annotationsMap.set(updatedAnn.id, updatedAnn)
    annotationsRTree.setItem(getRangeFromAnnotation(updatedAnn))
    recordAnnotationProperties(updatedAnn.id)
  }

  /**
   * Clear the store and set all annotations.
   */
  const setAnnotations = async (
    range: FramesRange,
    annotations: V2AnnotationPayload[],
  ): Promise<void> => {
    annotationsWithProperties.value = {}

    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.init()

      await annotationPackageManager.initAnnotations(range, annotations)

      return
    }

    loadedFrames.value.clear()
    annotationsMap.clear()
    tagAnnotationsMap.clear()
    masksMap.clear()
    rasterLayersMap.clear()

    const rTreeAnnotationItems = new Map<string, RTreeAnnotation>()

    const rTreeTagsItems = new Map<string, RTreeAnnotation>()

    const rasterLayersList: V2AnnotationPayload[] = []

    annotations.sort(compareByZIndex).forEach((ann) => {
      if (isRasterLayer(ann.annotation_class_id)) {
        rasterLayersList.unshift(ann)
        return
      }

      if (isAnnotationMask(ann.annotation_class_id)) {
        masksMap.addLast(ann, true)
        recordAnnotationProperties(ann.id)
        return
      }

      if (isAnnotationTag(ann.annotation_class_id)) {
        tagAnnotationsMap.addLast(ann, true)
        rTreeTagsItems.set(ann.id, getRangeFromAnnotation(ann))
        recordAnnotationProperties(ann.id)
        return
      }
      annotationsMap.addLast(ann, true)
      rTreeAnnotationItems.set(ann.id, getRangeFromAnnotation(ann))
      recordAnnotationProperties(ann.id)
    })

    for (let i = range.from; i <= range.to; i++) {
      loadedFrames.value.add(i)
    }
    triggerRef(loadedFrames)

    annotationsMap.triggerRef()
    annotationsRTree.setItems(rTreeAnnotationItems)

    tagAnnotationsMap.triggerRef()
    tagsRTree.setItems(rTreeTagsItems)

    masksMap.triggerRef()
    rasterLayersMap.addFromArray(rasterLayersList)
  }

  /**
   * Initialize the annotations using the metadata without frame data payload.
   */
  const pushMetadata = (range: FramesRange, metadatas: AnnotationMetadata[]): void => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.init()

      annotationPackageManager.pushMetadata(range, metadatas)
      metadataIsLoaded.value = true

      return
    }

    const rTreeAnnotationItems = new Map<string, RTreeAnnotation>()

    const rTreeTagsItems = new Map<string, RTreeAnnotation>()

    const rasterLayersList: V2AnnotationPayload[] = []

    metadatas.sort(compareByZIndex).forEach((metadata) => {
      const frames: SequenceData['frames'] = {}
      const subFrames: SequenceData['sub_frames'] = {}

      const ann = {
        id: metadata.annotation_id,
        actors: metadata.actors,
        annotation_class_id: metadata.annotation_class_id,
        annotation_group_id: metadata.annotation_group_id,
        z_index: metadata.z_index,
        context_keys: metadata.context_keys,
        properties: metadata.properties,
        data: {
          interpolated: metadata.interpolated,
          hidden_areas: metadata.hidden_areas,
          segments: metadata.segments,
          frames,
          sub_frames: subFrames,
          global_sub_types: metadata.global_sub_types,
        },
        frames_indices: new Set(metadata.frames_indices),
        sub_frames_indices: new Set(metadata.sub_frames_indices),
        auto_track_frames_indices: new Set(metadata.auto_track_frames_indices),
      }

      const range = getAnnotationRange(metadata.segments, 1)
      for (let i = range.startFrame; i <= range.endFrame; i++) {
        _framesNeedsLoading.value.add(i)
      }

      if (isRasterLayer(ann.annotation_class_id)) {
        rasterLayersList.unshift(ann)
        return
      }

      if (isAnnotationMask(ann.annotation_class_id)) {
        masksMap.addLast(ann, true)
        recordAnnotationProperties(ann.id)
        return
      }

      if (isAnnotationTag(ann.annotation_class_id)) {
        tagAnnotationsMap.addLast(ann, true)
        rTreeTagsItems.set(ann.id, getRangeFromAnnotation(ann))
        recordAnnotationProperties(ann.id)
        return
      }
      annotationsMap.addLast(ann, true)
      rTreeAnnotationItems.set(ann.id, getRangeFromAnnotation(ann))
      recordAnnotationProperties(ann.id)
    })

    triggerRef(_framesNeedsLoading)
    metadataIsLoaded.value = true

    annotationsMap.triggerRef()
    annotationsRTree.setItems(rTreeAnnotationItems)

    tagAnnotationsMap.triggerRef()
    tagsRTree.setItems(rTreeTagsItems)

    masksMap.triggerRef()
    rasterLayersMap.addFromArray(rasterLayersList)
  }

  /**
   * Assign the frame data to the annotations in the given range and mark frame as loaded.
   */
  const pushFramesData = (range: FramesRange, framesData: PaginatedAnnotationPayload[]): void => {
    annotationsWithProperties.value = {}

    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.pushFramesData(range, framesData)

      return
    }

    framesData.forEach((frameData) => {
      const ann = getAnnotation(frameData.annotation_id)
      if (!ann) {
        throw new Error('Annotation not found')
      }

      Object.keys(frameData.frames).forEach((frameIndex) => {
        if (!isVideoAnnotationDataPayload(ann.data)) {
          return
        }

        _framesNeedsLoading.value.delete(+frameIndex)
        ann.data.frames[frameIndex] = frameData.frames[frameIndex]
      })

      Object.keys(frameData.sub_frames).forEach((frameIndex) => {
        if (!isVideoAnnotationDataPayload(ann.data)) {
          return
        }

        _framesNeedsLoading.value.delete(+frameIndex)
        ann.data.sub_frames[frameIndex] = frameData.sub_frames[frameIndex]
      })
    })

    triggerRef(_framesNeedsLoading)

    annotationsMap.triggerRef()

    for (let i = range.from; i <= range.to; i++) {
      loadedFrames.value.add(i)
    }
    triggerRef(loadedFrames)
  }

  /**
   * Remove the frames data keeping the given range in the memory and mark the frames as needing loading.
   * We use an array to keep ranges to support a multi-slot layout.
   */
  const flushFramesData = (rangesToKeep: FramesRange[]): void => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.flushFramesData(rangesToKeep)

      return
    }

    for (const i of loadedFrames.value.values()) {
      const rangeToKeep = rangesToKeep.find((range) => range.from <= i && range.to >= i)
      if (rangeToKeep) {
        continue
      }

      loadedFrames.value.delete(i)
      _framesNeedsLoading.value.add(i)

      const anns = getAllIds([i, i], [0, Object.keys(itemSlotIndexMap.value).length - 1])
      anns.forEach(({ id }) => {
        const ann = getAnnotation(id)
        if (!ann) {
          throw new Error('Annotation not found')
        }

        if (!isVideoAnnotationDataPayload(ann.data)) {
          return
        }

        delete ann.data.frames[i]
        delete ann.data.sub_frames[i]
      })
    }

    triggerRef(_framesNeedsLoading)
    triggerRef(loadedFrames)
    annotationsMap.triggerRef()
  }

  const clearAnnotations = (): void => {
    metadataIsLoaded.value = false
    loadedFrames.value.clear()
    triggerRef(loadedFrames)
    _framesNeedsLoading.value.clear()
    triggerRef(_framesNeedsLoading)

    tagAnnotationsMap.clear()
    annotationsMap.clear()
    masksMap.clear()
    rasterLayersMap.clear()
    annotationsRTree.clear()
    tagsRTree.clear()

    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.clear()
      return
    }
  }

  const annotationsLoadingStatus = ref<LoadingStatus>(LoadingStatus.Unloaded)
  const error = ref<ErrorWithMessage | ParsedValidationError>()

  const isAnnotationsLoading = computed(
    () => annotationsLoadingStatus.value === LoadingStatus.Loading,
  )

  const isAnnotationsLoadingByParts = ref<boolean>(false)

  const maybeHideSubAnnotations = (): void => {
    if (annotationsEntries.value.length > 100 && workviewSettings.renderSubAnnotations) {
      workviewSettings.toggleRenderedSubAnnotations()
    }
  }

  type loadAnnotationsRequest = (params?: {
    limitBySize?: number
    offset?: number
  }) => Promise<ParsedError | { data: V2AnnotationPayload[] | null }>

  /**
   * Loads all item annotations making multiple requests.
   * Limits response size for every request by 10MB to avoid timeouts.
   */

  const loadAnnotationsByParts = async (
    datasetItemId: string,
    request: loadAnnotationsRequest,
    annotationsAcc: V2AnnotationPayload[] = [],
  ): Promise<V2AnnotationPayload[] | ErrorWithMessage | ParsedValidationError | void> => {
    const response = await request({
      limitBySize: ANNOTATIONS_SIZE_LIMIT_BYTES,
      offset: annotationsAcc.length,
    })

    if (datasetItemId !== itemsStore.currentItemId) {
      return
    }

    if ('data' in response && response.data !== null) {
      if (response.data.length == 0) {
        return annotationsAcc
      }
      const newAcc = annotationsAcc.concat(response.data)
      return loadAnnotationsByParts(datasetItemId, request, newAcc)
    }
    if ('error' in response) {
      return response.error
    }
    return
  }

  const loadAnnotationsInternal = async (
    datasetItemId: string,
    request: loadAnnotationsRequest,
  ): Promise<ErrorWithMessage | ParsedValidationError | void> => {
    const response = await request()
    mark(GET_RESPONSE_WITH_ANNOTATIONS)

    // Verify that the annotations you get are related to the active item
    if (datasetItemId !== itemsStore.currentItemId) {
      return
    }

    let annotations: V2AnnotationPayload[]

    if ('data' in response && response.data !== null) {
      annotations = response.data
    } else if ('error' in response && response.error.code == ErrorCodes.TIMEOUT) {
      isAnnotationsLoadingByParts.value = true
      const annotationsOrError = await loadAnnotationsByParts(datasetItemId, request)
      isAnnotationsLoadingByParts.value = false
      if (annotationsOrError instanceof Array) {
        annotations = annotationsOrError
      } else {
        return annotationsOrError
      }
    } else if ('error' in response) {
      return response.error
    } else {
      return
    }

    await setAnnotations(
      {
        from: 0,
        to: currentItemStore.activeSlotFramesCount,
      },
      annotations,
    )
    maybeHideSubAnnotations()
  }

  const datasetStore = useDatasetStore()

  const { shouldUsePaginatedAnnotationsV2 } = useShouldPaginateAnnotations()
  /**
   * Loads annotations for a specific v2 dataset item id.
   *
   * Once loaded, the annotations will be assigned to the `annotations` ref
   * returned by this composable, overriding the previous value.
   *
   * Since loading can take a bit, and in the meantime, the user might switch
   * to a different dataset item, once the data is received, we check if the
   * item has changed, in which case, this function is a no-op.
   *
   * The annotation group ids expected to be passed in are defined as a filter,
   * but actually act in a way where the result will be a completely separate
   * collection, not a subset of the full annotations, so it has been ticketed
   * to be separated out into `loadAnnotationGroup`.
   *
   * https://linear.app/v7labs/issue/MOD-700/
   */
  const loadAnnotations = async (
    datasetItemId: string,
  ): Promise<ErrorWithMessage | ParsedValidationError | void> => {
    if (shouldUsePaginatedAnnotationsV2.value) {
      /*
       * When using pagination, all calls to load annotations should go through
       * the pagination store. This function will load 100% of the annotations
       * for an item, which will cause huge issues for long videos with
       * 100k+ annotations.
       */
      throw new Error('Should not load all annotations at once when using pagination.')
    }
    const dataset = datasetStore.dataset

    if (!dataset) {
      throw new Error('Dataset not available')
    }

    annotationsLoadingStatus.value = LoadingStatus.Loading
    const request: loadAnnotationsRequest = (params = {}) =>
      loadV2Annotations({
        teamSlug: dataset.team_slug,
        datasetSlug: dataset.slug,
        datasetItemId,
        annotationGroupIds: workflowInWorkviewStore.annotationGroupIds,
        ...params,
      })
    const response = await loadAnnotationsInternal(datasetItemId, request)
    annotationsLoadingStatus.value = LoadingStatus.Loaded
    return response
  }

  const loadAnnotationsSnapshot = async (
    datasetItemId: string,
    snapshotId: string,
  ): Promise<ErrorWithMessage | ParsedValidationError | void> => {
    const dataset = datasetStore.dataset

    if (!dataset) {
      throw new Error('Dataset not available to load annotations snapshot')
    }

    annotationsLoadingStatus.value = LoadingStatus.Loading
    const request: loadAnnotationsRequest = (params = {}) =>
      loadV2AnnotationsSnapshot({
        teamSlug: dataset.team_slug,
        datasetItemId,
        snapshotId,
        ...params,
      })
    const response = await loadAnnotationsInternal(datasetItemId, request)
    annotationsLoadingStatus.value = LoadingStatus.Loaded
    return response
  }

  const updateStoreAnnotation = (
    ann: Omit<V2AnnotationPayload, 'actors' | 'annotation_group_id'>,
    updateMeta?: UpdateMeta,
  ): void => {
    const isMaskAnnotation = isAnnotationMask(ann.annotation_class_id)
    const isTagAnnotation = isAnnotationTag(ann.annotation_class_id)
    const oldAnn = getAnnotation(ann.id)

    if (!oldAnn) {
      console.error(
        'useV2AnnotationsStore, updateStoreAnnotation trying to update unknown annotation!',
      )
      return
    }

    if (ann.properties) {
      oldAnn.properties = ann.properties
    }

    const updatedAnn = featuresStore.featureFlags.ANNOTATIONS_PAGINATION
      ? updateMetadataIndices(
          {
            ...oldAnn,
            annotation_class_id: ann.annotation_class_id,
            data: ann.data,
            id: ann.id,
          },
          updateMeta,
        )
      : {
          ...oldAnn,
          annotation_class_id: ann.annotation_class_id,
          data: ann.data,
          id: ann.id,
        }

    if (isRasterLayer(ann.annotation_class_id)) {
      rasterLayersMap.set(ann.id, updatedAnn)
      return
    }

    if (isMaskAnnotation) {
      masksMap.set(ann.id, updatedAnn)
      return
    }

    if (isTagAnnotation) {
      tagAnnotationsMap.set(ann.id, updatedAnn)
      tagsRTree.setItem(getRangeFromAnnotation(ann))

      return
    }

    annotationsMap.set(ann.id, updatedAnn)
    annotationsRTree.setItem(getRangeFromAnnotation(ann))
  }

  /**
   * Reserves and returns the next instance id value for an annotation containing the instance ID
   * subtype.
   *
   * The instanceID subannotation type is an auto-incrementing integer scoped to a team record,
   * that grows with every new annotation being added.
   *
   * Since our annotation create requests do not return the created data, we cannot know what the
   * next value is and cannot have the UI render the auto-generated one. Instead, we use this action
   * to prefetch and reserve it, so the optimistic UI can render it.
   *
   * We aim to use this when initializing a new annotation.
   */
  const getInstanceId = async (): Promise<getInstanceIdResponse> => {
    if (!itemsStore.currentItemId) {
      throw new CreateActionError('Item not set')
    }

    if (!datasetStore.dataset) {
      throw new CreateActionError('Creating annotation without dataset set.')
    }

    // Auto-Start Workflow if not yet started and creating the first annotation
    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }

    const params: Parameters<typeof generateInstanceId>[0] = {
      stage_id: stageId,
      teamSlug: datasetStore.dataset.team_slug,
      datasetItemId: itemsStore.currentItemId,
    }
    return await generateInstanceId(params)
  }

  const createZIndexForAnnotation = (ann: V2AnnotationPayload): undefined | null | number => {
    if (isAnnotationTag(ann.annotation_class_id)) {
      return null
    }

    // API assign z_index property by itself for non-tag video annotations.
    if ('frames' in ann.data) {
      return undefined
    }

    // assign z_index property for non-tag image annotations.
    // NOTE: keep the old version to be safe, and reduce the API response time.
    return annotationsMap.size.value
  }

  const {
    unsavedAnnotationCount,
    hasUnsavedAnnotation,
    unsavedAnnotationRequests,
    dispatchAnnotationCommand,
  } = useSharedAnnotationRequestCounter()

  const createAnnotation = async (ann: V2AnnotationPayload): Promise<CreateAnnotationResponse> => {
    if (!itemsStore.currentItemId) {
      throw new CreateActionError('Item not set')
    }

    if (!datasetStore.dataset) {
      throw new CreateActionError('Creating annotation without dataset set.')
    }

    // Auto-Start Workflow if not yet started and creating the first annotation
    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }

    // optimistic UI
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.createAnnotation(ann)
    } else {
      if (isAnnotationMask(ann.annotation_class_id)) {
        masksMap.addFirst(ann)
      } else if (isAnnotationTag(ann.annotation_class_id)) {
        tagAnnotationsMap.addFirst(ann)
        tagsRTree.setItem(getRangeFromAnnotation(ann))
      } else {
        annotationsMap.addFirst(ann)
        annotationsRTree.setItem(getRangeFromAnnotation(ann))
      }
    }

    recordAnnotationProperties(ann.id)

    const params: Parameters<typeof sendV2Commands>[0] = {
      commands: [
        {
          type: 'create_annotation',
          data: {
            annotation_class_id: ann.annotation_class_id,
            annotation_group_id: ann.annotation_group_id,
            annotation_id: ann.id,
            data: ann.data,
            context_keys: ann.context_keys,
            metadata: {},
            stage_id: stageId,
            z_index: featuresStore.featureFlags.ANNOTATIONS_PACKAGE
              ? annotationPackageManager.createZIndexForAnnotation(ann)
              : createZIndexForAnnotation(ann),
            properties: ann.properties,
          },
        },
      ],
      teamSlug: datasetStore.dataset.team_slug,
      datasetItemId: itemsStore.currentItemId,
    }

    const response = await dispatchAnnotationCommand(ann.id, params)
    if ('data' in response) {
      return { data: ann as V2AnnotationPayload }
    }
    error.value = response.error
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.removeAnnotation(ann.id)
    } else {
      if (isAnnotationMask(ann.annotation_class_id)) {
        masksMap.remove(ann.id)
      } else if (isAnnotationTag(ann.annotation_class_id)) {
        tagAnnotationsMap.remove(ann.id)
        tagsRTree.removeItem(ann.id)
      } else {
        annotationsMap.remove(ann.id)
        annotationsRTree.removeItem(ann.id)
      }
    }

    recordAnnotationProperties(ann.id)

    return response
  }

  /**
   * The `snapshotIdForAnnotationCopy` variable is needed because when we copy
   * an annotation from a snapshot stage to present mode, the snapshot ID is lost.
   *
   * `workflowInWorkviewStore.selectedStageInstance.data.exit_snapshot_id` is reset
   * to `null` since `selectedStageInstance` changes, so we need to store the original
   * snapshot ID somewhere.
   *
   * It is used in the `duplicateAnnotation` function and passed to the `copy_annotation` command.
   */
  const snapshotIdForAnnotationCopy = ref<string | undefined>(undefined)

  const setSnapshotIdForAnnotationCopy = (snapshotId: string | undefined): void => {
    snapshotIdForAnnotationCopy.value = snapshotId
  }

  const duplicateAnnotation = async (
    {
      sourceDatasetItemId,
      sourceAnnotationId,
      newAnnotation,
    }: {
      sourceDatasetItemId: string
      sourceAnnotationId: string
      newAnnotation: Omit<V2AnnotationPayload, 'actors'>
    },
    duplicateMeta?: DuplicateMeta,
  ): Promise<{ success: boolean }> => {
    const currentItemId = itemsStore.currentItemId
    if (!currentItemId) {
      throw new Error('Item not set')
    }

    if (!datasetStore.dataset) {
      throw new Error('Dataset not set')
    }

    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }

    const sourceAnnotation = featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.getAnnotation(sourceAnnotationId)
      : annotationsMap.get(sourceAnnotationId)

    const newAnnotationWithActors = featuresStore.featureFlags.ANNOTATIONS_PAGINATION
      ? updateMetadataIndices(
          {
            ...newAnnotation,
            actors: sourceAnnotation?.actors ?? [],
            frames_indices: new Set(sourceAnnotation?.frames_indices),
            sub_frames_indices: new Set(sourceAnnotation?.sub_frames_indices),
            auto_track_frames_indices: new Set(sourceAnnotation?.auto_track_frames_indices),
          },
          duplicateMeta,
        )
      : {
          ...newAnnotation,
          actors: sourceAnnotation?.actors ?? [],
        }

    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.createAnnotation(newAnnotationWithActors)
      annotationPackageManager.reorderAnnotation(
        { id: newAnnotation.id },
        { id: sourceAnnotationId },
        'up',
      )
    } else {
      annotationsMap.addFirst(newAnnotationWithActors)
      annotationsRTree.setItem(getRangeFromAnnotation(newAnnotation))
    }
    recordAnnotationProperties(newAnnotation.id)

    const response = await dispatchAnnotationCommand(newAnnotation.id, {
      commands: [
        {
          type: 'copy_annotation',
          data: {
            new_annotation_id: newAnnotation.id,
            annotation_id: sourceAnnotationId,
            target_annotation_group_id: newAnnotation.annotation_group_id,
            // overrides the original annotation slot position
            context_keys_override: { slot_names: [currentSlotName.value] },
            shift_amount: duplicateMeta?.segmentShiftAmount,
            segment_length_override: duplicateMeta?.segmentLengthToOne ? 1 : undefined,
            first_keyframe_only: duplicateMeta?.firstKeyFrameOnly,
            stage_id: stageId,
            source_dataset_item_id: sourceDatasetItemId,
            source_snapshot_id: snapshotIdForAnnotationCopy.value,
          },
        },
      ],
      teamSlug: datasetStore.dataset.team_slug,
      datasetItemId: currentItemId,
    })

    if ('data' in response) {
      return response.data
    }

    error.value = response.error
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.removeAnnotation(newAnnotation.id)
    } else {
      if (isAnnotationMask(newAnnotation.annotation_class_id)) {
        masksMap.remove(newAnnotation.id)
      } else if (isAnnotationTag(newAnnotation.annotation_class_id)) {
        tagAnnotationsMap.remove(newAnnotation.id)
        tagsRTree.removeItem(newAnnotation.id)
      } else {
        annotationsMap.remove(newAnnotation.id)
        annotationsRTree.removeItem(newAnnotation.id)
      }
    }

    recordAnnotationProperties(newAnnotation.id)

    return { success: false }
  }

  // This function build video data where frames and sub_frames fields contain only changed indices
  // to reduce payload sent to the server
  const getChangedVideoAnnotationData = (
    annotation: Pick<V2AnnotationPayload, 'data'>,
    updateMeta?: UpdateMeta,
  ): PartialVideoDataRequestPayload => {
    if (!('frames' in annotation.data)) {
      throw new Error('annotation is not for a video')
    }

    const { frames, sub_frames, ...rest } = annotation.data
    const diffAnnotationDataPayload: PartialVideoDataRequestPayload = rest

    if (updateMeta?.updatedFramesIndices) {
      diffAnnotationDataPayload.frames = {}
      for (const key of updateMeta.updatedFramesIndices.values()) {
        diffAnnotationDataPayload.frames[key] = annotation.data.frames[key] || null
      }
    }

    if (updateMeta?.updatedSubFramesIndices && annotation.data.sub_frames) {
      diffAnnotationDataPayload.sub_frames = {}
      for (const key of updateMeta.updatedSubFramesIndices.values()) {
        diffAnnotationDataPayload.sub_frames[key] = annotation.data.sub_frames[key] || null
      }
    }

    return diffAnnotationDataPayload
  }

  const buildSegmentShiftParams = (
    annotationId: string,
    itemId: string,
    stageId: string,
    teamSlug: string,
    shiftAmount: number,
  ): Parameters<typeof sendV2Commands>[0] => ({
    commands: [
      {
        type: 'shift_sequence_annotation_segments',
        data: {
          shift_amount: shiftAmount,
          annotation_id: annotationId,
          stage_id: stageId,
        },
      },
    ],
    teamSlug,
    datasetItemId: itemId,
  })

  const buildUpdateParams = (
    annotation: Omit<V2AnnotationPayload, 'actors' | 'annotation_group_id'>,
    itemId: string,
    stageId: string,
    teamSlug: string,
    updateMeta?: UpdateMeta,
  ): Parameters<typeof sendV2Commands>[0] => {
    const payload:
      | Omit<UpdateAnnotationCommand, 'id' | 'user_id'>
      | Omit<UpdateSequenceAnnotationCommand, 'id' | 'user_id'> =
      'frames' in annotation.data
        ? {
            type: 'update_sequence_annotation',
            data: {
              annotation_class_id: annotation.annotation_class_id,
              data: getChangedVideoAnnotationData(annotation, updateMeta),
              annotation_id: annotation.id,
              context_keys: annotation.context_keys,
              stage_id: stageId,
              metadata: {},
              properties: cloneDeep(annotation.properties),
            },
          }
        : {
            type: 'update_annotation',
            data: {
              annotation_class_id: annotation.annotation_class_id,
              data: annotation.data,
              annotation_id: annotation.id,
              context_keys: annotation.context_keys,
              stage_id: stageId,
              metadata: {},
              properties: cloneDeep(annotation.properties),
            },
          }

    return {
      commands: [payload],
      teamSlug: teamSlug,
      datasetItemId: itemId,
    }
  }

  const updateAnnotation = async (
    payload: StoreAnnotationWithItemId,
  ): Promise<UpdateAnnotationResponse> => {
    if (!datasetStore.dataset) {
      throw new UpdateActionError('Trying to update annotation without dataset set')
    }

    const { storeAnnotation: ann, itemId, updateMeta } = payload

    if (!itemId) {
      throw new UpdateActionError('Item not set')
    }

    if (!ann.id) {
      return constructError(ErrorCodes.UPDATING_ANNOTATION_WITHOUT_ID)
    }

    const previousStoreAnnotation = cloneDeep(getAnnotation(ann.id))
    if (!previousStoreAnnotation) {
      throw new UpdateActionError('Trying to update unknown annotation')
    }

    let storeAnnotation = ann
    if (
      isVideoAnnotationDataPayload(ann.data) &&
      isVideoAnnotationDataPayload(previousStoreAnnotation.data)
    ) {
      // Properties require to have all applied properties duplicated for the first frame
      storeAnnotation = willApplyPropertyCorrection(
        ann,
        previousStoreAnnotation?.data?.segments,
        previousStoreAnnotation?.annotation_class_id,
      )
    }

    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }

    // optimistic UI
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.updateAnnotation({
        ...previousStoreAnnotation,
        ...storeAnnotation,
      })
    } else {
      updateStoreAnnotation(storeAnnotation, updateMeta)
      if (payload.updateMeta?.segmentShiftAmount || payload.updateMeta?.hasChangedLength) {
        if (isAnnotationTag(storeAnnotation.annotation_class_id)) {
          tagsRTree.setItem(getRangeFromAnnotation(storeAnnotation))
        } else {
          annotationsRTree.setItem(getRangeFromAnnotation(storeAnnotation))
        }
      }
    }

    recordAnnotationProperties(storeAnnotation.id)

    const params = updateMeta?.segmentShiftAmount
      ? buildSegmentShiftParams(
          storeAnnotation.id,
          itemId,
          stageId,
          datasetStore.dataset.team_slug,
          updateMeta.segmentShiftAmount,
        )
      : buildUpdateParams(
          storeAnnotation,
          itemId,
          stageId,
          datasetStore.dataset.team_slug,
          updateMeta,
        )

    // We need to remove properties that were set to null (to remove them on the API side)
    if (storeAnnotation.properties) {
      Object.keys(storeAnnotation.properties).forEach((key) => {
        if (storeAnnotation.properties && !storeAnnotation.properties[Number(key)]) {
          delete storeAnnotation.properties[Number(key)]
        }
      })
    }

    recordAnnotationProperties(storeAnnotation.id)

    const response = await dispatchAnnotationCommand(storeAnnotation.id, params)

    if ('data' in response) {
      return response
    }
    // rollback
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.updateAnnotation(previousStoreAnnotation)
    } else {
      updateStoreAnnotation({ ...previousStoreAnnotation })
    }
    error.value = response.error
    recordAnnotationProperties(storeAnnotation.id)

    return response
  }

  const getRasterLayerAnnotation = async (
    teamSlug: string,
    datasetSlug: string,
    datasetItemId: string,
  ): Promise<V2AnnotationPayload | null> => {
    // TODO: MED-232
    // This is a temporary solution, which is a result of the current API limitations.
    // When we create the first mask annotation, the backend automatically creates
    // additional raster_layer annotation, but it doesn't return its ID.
    // So we need to fire additional request to get it.
    // The only way to do it is to load all annotations and find the raster_layer there.
    const response = await loadV2Annotations({
      teamSlug,
      datasetSlug,
      datasetItemId,
    })

    if ('data' in response) {
      const videoRasterLayerAnnotation = response.data?.find(isVideoRasterLayerAnnotation)
      if (videoRasterLayerAnnotation) {
        return videoRasterLayerAnnotation
      }
    }

    return null
  }

  const buildUpdateParamsForRasterLayerAnnotation = (
    annotationId: string,
    annotationData: PartialVideoDataRequestPayload,
    slotName: string,
    itemId: string,
    stageId: string,
    teamSlug: string,
  ): Parameters<typeof sendV2Commands>[0] => ({
    commands: [
      {
        type: 'update_sequence_annotation',
        data: {
          annotation_id: annotationId,
          data: annotationData,
          context_keys: {
            slot_names: [slotName],
          },
          z_index: null,
          stage_id: stageId,
          metadata: {},
        },
      },
    ],
    teamSlug: teamSlug,
    datasetItemId: itemId,
  })

  const updateVideoRasterLayerAnnotation = async (
    videoRaster: VideoRaster,
    annotationData: PartialVideoDataRequestPayload,
  ): Promise<UpdateAnnotationResponse> => {
    if (!datasetStore.dataset) {
      throw new UpdateActionError('Trying to update annotation without dataset set')
    }

    if (!itemsStore.currentItemId) {
      throw new UpdateActionError('Item not set')
    }

    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }

    let annotationId: string | null = null

    if (videoRaster.idSyncedWithApi) {
      annotationId = videoRaster.idSyncedWithApi
    } else {
      const videoRasterLayerAnnotation = await getRasterLayerAnnotation(
        datasetStore.dataset.team_slug,
        datasetStore.dataset.slug,
        itemsStore.currentItemId,
      )

      if (videoRasterLayerAnnotation) {
        annotationId = videoRasterLayerAnnotation.id
        videoRaster.idSyncedWithApi = videoRasterLayerAnnotation.id
      }
    }

    if (!annotationId) {
      throw new UpdateActionError('Video raster layer annotation ID not available')
    }

    const params = buildUpdateParamsForRasterLayerAnnotation(
      annotationId,
      annotationData,
      videoRaster.getFileSlotName(),
      itemsStore.currentItemId,
      stageId,
      datasetStore.dataset.team_slug,
    )

    const response = await dispatchAnnotationCommand(annotationId, params)
    return response
  }

  const getAnnotationSiblings = (
    annotationSlotName: string,
    annotationId: string,
  ): {
    before: string | null
    after: string | null
  } => {
    const slotAnnotations = annotationsMap.values.value.filter(
      (ann) => ann.context_keys?.slot_names?.[0] === annotationSlotName,
    )
    const slotAnnotationIds = slotAnnotations.map((ann) => ann.id)
    const slotAnnotationIndex = slotAnnotationIds.indexOf(annotationId)

    return {
      before: slotAnnotationIds[slotAnnotationIndex - 1] ?? null,
      after: slotAnnotationIds[slotAnnotationIndex + 1] ?? null,
    }
  }

  const doReorderAnnotation = async (
    payload: StoreAnnotationWithItemId,
  ): Promise<UpdateAnnotationResponse> => {
    if (!datasetStore.dataset) {
      throw new ReorderActionError('Trying to update annotation without dataset set')
    }

    const { storeAnnotation, itemId } = payload

    if (!itemId) {
      throw new ReorderActionError('Item not set')
    }

    if (!storeAnnotation.id) {
      return constructError(ErrorCodes.UPDATING_ANNOTATION_WITHOUT_ID)
    }

    const type = getAnnotationMainTypeByClassId(storeAnnotation?.annotation_class_id)
    if (!type) {
      throw new ReorderActionError('No annotation type')
    }

    if (['tag', 'mask', 'raster_layer'].includes(type)) {
      throw new ReorderActionError(`Cannot reorder annotation of type ${type}`)
    }

    const previousStoreAnnotation = getAnnotation(storeAnnotation.id)
    if (!previousStoreAnnotation) {
      throw new ReorderActionError('Trying to reorder unknown annotation')
    }

    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }

    const siblings = getAnnotationSiblings(
      storeAnnotation.context_keys?.slot_names?.[0] ?? '',
      storeAnnotation.id,
    )

    // which annotation is the new neighbor of annotation being reordered
    let newNeighborAnnotationId: ReorderAnnotationCommand['data']['new_neighbor_annotation_id']
    // the position to place annotation being reordered relative to its new neighbor
    let position: ReorderAnnotationCommand['data']['position']

    if (siblings.before) {
      newNeighborAnnotationId = siblings.before
      position = 'before'
    } else if (siblings.after) {
      newNeighborAnnotationId = siblings.after
      position = 'after'
    } else {
      throw new ReorderActionError('Annotation does not have neighbors')
    }

    const response = await dispatchAnnotationCommand(storeAnnotation.id, {
      commands: [
        {
          type: 'reorder_annotation',
          data: {
            annotation_id: storeAnnotation.id,
            new_neighbor_annotation_id: newNeighborAnnotationId,
            position: position,
            stage_id: stageId,
          },
        },
      ],
      teamSlug: datasetStore.dataset.team_slug,
      datasetItemId: itemId,
    })

    if ('data' in response) {
      return response
    }

    error.value = response.error
    return response
  }

  const doReorderAnnotationV2 = async (
    payload: StoreAnnotationWithItemId,
    referenceId: string,
    direction: 'up' | 'down',
  ): Promise<UpdateAnnotationResponse> => {
    if (!datasetStore.dataset) {
      throw new ReorderActionError('Trying to update annotation without dataset set')
    }

    const { storeAnnotation, itemId } = payload

    if (!itemId) {
      throw new ReorderActionError('Item not set')
    }

    if (!storeAnnotation.id) {
      return constructError(ErrorCodes.UPDATING_ANNOTATION_WITHOUT_ID)
    }

    const type = getAnnotationMainTypeByClassId(storeAnnotation?.annotation_class_id)
    if (!type) {
      throw new ReorderActionError('No annotation type')
    }

    if (['tag', 'mask', 'raster_layer'].includes(type)) {
      throw new ReorderActionError(`Cannot reorder annotation of type ${type}`)
    }

    const previousStoreAnnotation = getAnnotation(storeAnnotation.id)
    if (!previousStoreAnnotation) {
      throw new ReorderActionError('Trying to reorder unknown annotation')
    }

    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }
    // the position to place annotation being reordered relative to its new neighbor
    let position: ReorderAnnotationCommand['data']['position']

    if (direction === 'up') {
      position = 'after'
    } else if (direction === 'down') {
      position = 'before'
    } else {
      throw new ReorderActionError('Annotation does not have neighbors')
    }

    const response = await dispatchAnnotationCommand(storeAnnotation.id, {
      commands: [
        {
          type: 'reorder_annotation',
          data: {
            annotation_id: storeAnnotation.id,
            new_neighbor_annotation_id: referenceId,
            position: position,
            stage_id: stageId,
          },
        },
      ],
      teamSlug: datasetStore.dataset.team_slug,
      datasetItemId: itemId,
    })

    if ('data' in response) {
      return response
    }

    error.value = response.error
    return response
  }

  const handleReorderError = (
    ann: V2AnnotationPayload,
    originalBefore: V2AnnotationPayload | null,
  ): void => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.reorderAnnotationOnError(ann, originalBefore)
      return
    }

    annotationsMap.remove(ann.id)
    if (originalBefore) {
      annotationsMap.addAfter(ann, originalBefore.id)
    } else {
      annotationsMap.addFirst(ann)
    }

    if (!sharedLayout.value) {
      return
    }

    sharedLayout.value.layout.viewsList.forEach((view: View) => {
      setAnnotationsForCurrentFrameIndex(
        view,
        currentFrameInViewAnnotationsPerSlot.value[view.name] || [],
        allRasterLayers.value,
        getAnnotation,
        allowedClassIds.value,
        editorClassesById.value,
      )
    })
  }

  const getNextAnnotationForCurrentFrame = (
    annId: string,
    slotName: string,
  ): V2AnnotationPayload | null => {
    const annIndex =
      annotationPackageManager.currentFrameAnnotationsFrameDataPerSlot.value[
        slotName
      ]?.orderedAnnotationIds.indexOf(annId)

    if (annIndex === undefined || annIndex === -1) {
      return null
    }

    const nextAnnId =
      annotationPackageManager.currentFrameAnnotationsFrameDataPerSlot.value[slotName]
        ?.orderedAnnotationIds[annIndex + 1]

    if (!nextAnnId) {
      return null
    }

    return getAnnotation(nextAnnId)
  }
  const getPreviousAnnotationForCurrentFrame = (
    annId: string,
    slotName: string,
  ): V2AnnotationPayload | null => {
    const annIndex =
      annotationPackageManager.currentFrameAnnotationsFrameDataPerSlot.value[
        slotName
      ]?.orderedAnnotationIds.indexOf(annId)

    if (annIndex === undefined || annIndex === -1) {
      return null
    }

    const prevAnnId =
      annotationPackageManager.currentFrameAnnotationsFrameDataPerSlot.value[slotName]
        ?.orderedAnnotationIds[annIndex - 1]

    if (!prevAnnId) {
      return null
    }

    return getAnnotation(prevAnnId)
  }

  /** reorders `toReorder` either above or below
   * `reference` based on `direction` */
  const reorderAnnotation = async (
    toReorder: { id: string; zIndex: number | null },
    reference: { id: string },
    /** needed to determine if `toReorder`
     * will be placed before or after `reference`*/
    direction: 'up' | 'down',
  ): Promise<void> => {
    const originalBefore = featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? getPreviousAnnotationForCurrentFrame(toReorder.id, currentSlotName.value)
      : annotationsMap.getBefore(toReorder.id)
    const ann = featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.getAnnotation(toReorder.id)
      : annotationsMap.get(toReorder.id)
    if (!ann || isAnnotationTag(ann.annotation_class_id)) {
      return
    }

    if (toReorder.id === reference.id) {
      return
    }

    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.reorderAnnotation(toReorder, reference, direction)
    } else {
      annotationsMap.remove(toReorder.id)
      if (direction === 'down') {
        annotationsMap.addAfter(ann, reference.id)
      } else {
        annotationsMap.addBefore(ann, reference.id)
      }
    }

    if (!itemsStore.currentItemId) {
      throw new Error('Item not set')
    }

    try {
      if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
        const response = await doReorderAnnotationV2(
          {
            storeAnnotation: ann,
            itemId: itemsStore.currentItemId,
          },
          reference.id,
          direction,
        )

        if ('error' in response) {
          handleReorderError(ann, originalBefore)
        }
      } else {
        const response = await doReorderAnnotation({
          storeAnnotation: ann,
          itemId: itemsStore.currentItemId,
        })

        if ('error' in response) {
          handleReorderError(ann, originalBefore)
        }
      }
    } catch {
      handleReorderError(ann, originalBefore)
    }
  }

  /**
   * Moves the annotation by its id to the bottom of the ordered list
   * @param annotationId
   * @returns
   */
  const moveBottom = async (annotationId: string): Promise<void> => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      const originalBefore = getPreviousAnnotationForCurrentFrame(
        annotationId,
        currentSlotName.value,
      )
      const ann = annotationPackageManager.getAnnotation(annotationId)
      if (!ann || isAnnotationTag(ann.annotation_class_id)) {
        return
      }

      annotationPackageManager.moveAnnotationBottom(annotationId)

      if (!itemsStore.currentItemId) {
        throw new Error('Item not set')
      }

      const prevAnn = getPreviousAnnotationForCurrentFrame(annotationId, currentSlotName.value)

      if (!prevAnn) {
        throw new Error('Previous annotation not found')
      }

      try {
        const response = await doReorderAnnotationV2(
          {
            storeAnnotation: ann,
            itemId: itemsStore.currentItemId,
          },
          prevAnn.id,
          'down',
        )

        if ('error' in response) {
          handleReorderError(ann, originalBefore)
        }
      } catch {
        handleReorderError(ann, originalBefore)
      }

      return
    }

    const originalBefore = annotationsMap.getBefore(annotationId)
    const ann = annotationsMap.get(annotationId)
    if (!ann || isAnnotationTag(ann.annotation_class_id)) {
      return
    }

    annotationsMap.remove(annotationId)
    annotationsMap.addLast(ann)

    if (!itemsStore.currentItemId) {
      throw new Error('Item not set')
    }

    try {
      const response = await doReorderAnnotation({
        storeAnnotation: ann,
        itemId: itemsStore.currentItemId,
      })

      if ('error' in response) {
        handleReorderError(ann, originalBefore)
      }
    } catch {
      handleReorderError(ann, originalBefore)
    }
  }

  /**
   * Moves the annotation by its id to the top of the ordered list
   * @param annotationId
   * @returns
   */
  const moveTop = async (annotationId: string): Promise<void> => {
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      const originalBefore = getPreviousAnnotationForCurrentFrame(
        annotationId,
        currentSlotName.value,
      )
      const ann = annotationPackageManager.getAnnotation(annotationId)
      if (!ann || isAnnotationTag(ann.annotation_class_id)) {
        return
      }

      annotationPackageManager.moveAnnotationTop(annotationId)

      if (!itemsStore.currentItemId) {
        throw new Error('Item not set')
      }

      const nextAnn = getNextAnnotationForCurrentFrame(annotationId, currentSlotName.value)

      if (!nextAnn) {
        throw new Error('Next annotation not found')
      }

      try {
        const response = await doReorderAnnotationV2(
          {
            storeAnnotation: ann,
            itemId: itemsStore.currentItemId,
          },
          nextAnn.id,
          'up',
        )

        if ('error' in response) {
          handleReorderError(ann, originalBefore)
        }
      } catch {
        handleReorderError(ann, originalBefore)
      }

      return
    }

    const originalBefore = annotationsMap.getBefore(annotationId)
    const ann = annotationsMap.get(annotationId)
    if (!ann || isAnnotationTag(ann.annotation_class_id)) {
      return
    }

    annotationsMap.remove(annotationId)
    annotationsMap.addFirst(ann)

    if (!itemsStore.currentItemId) {
      throw new Error('Item not set')
    }

    try {
      const response = await doReorderAnnotation({
        storeAnnotation: ann,
        itemId: itemsStore.currentItemId,
      })

      if ('error' in response) {
        handleReorderError(ann, originalBefore)
      }
    } catch {
      handleReorderError(ann, originalBefore)
    }
  }

  type removedAnnotation = {
    annotation: V2AnnotationPayload | null
    isTagAnnotation: boolean | null
    nextItemId: string | undefined
  }

  const removeAnnotation = (id: string): removedAnnotation => {
    const ann = getAnnotation(id)

    const isMaskAnnotation = ann && isAnnotationMask(ann.annotation_class_id)
    const isTagAnnotation = ann && isAnnotationTag(ann.annotation_class_id)

    // We need the next item id in the list to rollback the changes on fail
    // and insert the removed item back into the same position in the list
    const nextItemId = featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? getNextAnnotationForCurrentFrame(id, currentSlotName.value)?.id
      : isMaskAnnotation
        ? masksMap.getAfter(id)?.id
        : isTagAnnotation
          ? tagAnnotationsMap.getAfter(id)?.id
          : annotationsMap.getAfter(id)?.id

    // Optimistic UI: Remove annotation from state
    if (featuresStore.featureFlags.ANNOTATIONS_PACKAGE) {
      annotationPackageManager.removeAnnotation(id)
    } else {
      if (isMaskAnnotation) {
        masksMap.remove(id)
      } else if (isTagAnnotation) {
        tagAnnotationsMap.remove(id)
        tagsRTree.removeItem(id)
      } else {
        annotationsMap.remove(id)
        annotationsRTree.removeItem(id)
      }
    }

    recordAnnotationProperties(id)

    return { annotation: ann, isTagAnnotation, nextItemId }
  }

  const restoreAnnotation = ({
    annotation,
    isTagAnnotation,
    nextItemId,
  }: removedAnnotation): void => {
    if (!annotation) {
      return
    }

    const isMaskAnnotation = isAnnotationMask(annotation.annotation_class_id)

    if (isMaskAnnotation) {
      nextItemId ? masksMap.addBefore(annotation, nextItemId) : masksMap.addLast(annotation)
    } else if (isTagAnnotation) {
      nextItemId
        ? tagAnnotationsMap.addBefore(annotation, nextItemId)
        : tagAnnotationsMap.addLast(annotation)

      tagsRTree.setItem(getRangeFromAnnotation(annotation))
    } else {
      nextItemId
        ? annotationsMap.addBefore(annotation, nextItemId)
        : annotationsMap.addLast(annotation)

      annotationsRTree.setItem(getRangeFromAnnotation(annotation))
    }

    recordAnnotationProperties(annotation.id)
  }

  const deleteAnnotation = async (id: string): Promise<DeleteAnnotationResponse> => {
    // Annotation already deleted
    if (!getAnnotation(id)) {
      return { data: id }
    }

    const itemsStore = useDatasetItemsStore()

    if (!itemsStore.currentItemId) {
      throw new DeleteActionError('Item not set')
    }

    if (!datasetStore.dataset) {
      throw new DeleteActionError('Dataset not set')
    }

    const removedAnnotation = removeAnnotation(id)

    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }
    const params: Parameters<typeof sendV2Commands>[0] = {
      commands: [
        {
          type: 'delete_annotation',
          data: {
            annotation_id: id,
            stage_id: stageId,
          },
        },
      ],
      teamSlug: datasetStore.dataset.team_slug,
      datasetItemId: itemsStore.currentItemId,
    }

    const response = await dispatchAnnotationCommand(id, params)

    if ('error' in response) {
      restoreAnnotation(removedAnnotation)

      error.value = response.error
      return response
    }

    return { data: id }
  }

  const deleteAnnotations = async (ids: string[]): Promise<DeleteAnnotationsResponse> => {
    if (ids.length === 0) {
      return { data: ids }
    }

    const itemsStore = useDatasetItemsStore()

    if (!itemsStore.currentItemId) {
      throw new DeleteActionError('Item not set')
    }

    if (!datasetStore.dataset) {
      throw new DeleteActionError('Dataset not set')
    }

    const removedAnnotations = ids.map((id) => removeAnnotation(id))

    const stageId = workflowInWorkviewStore.assignedStageInstance?.stage?.id
    if (!stageId) {
      throw new CreateActionError('Stage not available.')
    }

    const params: Parameters<typeof sendV2Commands>[0] = {
      commands: [
        {
          type: 'delete_annotations',
          data: {
            annotation_ids: ids,
            stage_id: stageId,
          },
        },
      ],
      teamSlug: datasetStore.dataset.team_slug,
      datasetItemId: itemsStore.currentItemId,
    }

    const response = await sendV2Commands(params)

    if (!response.ok) {
      // rollback
      removedAnnotations
        .reverse()
        .forEach((removedAnnotation) => restoreAnnotation(removedAnnotation))

      error.value = response.error
      return response
    }

    return { data: ids }
  }

  const copyAnnotations = async (
    payload: CopyStoreAnnotationsParams,
  ): Promise<void | ErrorWithMessage> => {
    const response = await copyV2Annotations(payload)

    if ('data' in response) {
      setAnnotations({ from: 0, to: currentItemStore.activeSlotFramesCount }, response.data)
      return
    }

    const resolvedError =
      // we should not expect a validation error.
      // it means the frontend is sending wrong data to the backend.
      'isValidationError' in response.error
        ? {
            code: null,
            message: 'The application seems to be sending invalid data. Please contact support',
            backendMessage: null,
            detail: null,
            status: 422,
          }
        : response.error

    error.value = resolvedError

    return resolvedError
  }

  const hasAnnotation = (id: string): boolean => !!getAnnotation(id)

  return {
    allAnnotations: featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.allAnnotations
      : allAnnotations,
    allTags: featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.allTags
      : allTags,
    allMasks: featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.allMasks
      : allMasks,
    allRasterLayers: featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.allRasterLayers
      : allRasterLayers,
    currentFrameAnnotationsFrameDataPerSlot:
      annotationPackageManager.currentFrameAnnotationsFrameDataPerSlot,
    annotationsPerSlot: featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.annotationsPerSlot
      : {},

    framesReady: featuresStore.featureFlags.ANNOTATIONS_PACKAGE
      ? annotationPackageManager.parsedFrames
      : loadedFrames,

    hasAnnotation,
    hasAnnotationsWithProperties,
    authorAnnotationIdsMap,
    classAnnotationIdsMap,
    annotationsLoadingStatus,
    isAnnotationsLoading,
    isAnnotationsLoadingByParts,
    getNextAnnotationForCurrentFrame,
    getPreviousAnnotationForCurrentFrame,
    itemSlotIndexMap,
    // Current version of the computedWithControl + Vue3 has an issue with identifying
    // the type correctly.
    // This is a workaround to get the correct type.
    // DAR-5652 - to re-check if this is still needed
    currentFrameAllAnnotationsPerSlot: computed(() => currentFrameAllAnnotationsPerSlot.value),
    currentFrameInViewAnnotationsPerSlot: computed(
      () => currentFrameInViewAnnotationsPerSlot.value,
    ),

    pushMetadata,
    pushFramesData,
    framesNeedsLoading,
    flushFramesData,
    metadataIsLoaded,

    [ACTIONS.REORDER_ANNOTATION]: reorderAnnotation,
    [ACTIONS.MOVE_BOTTOM]: moveBottom,
    [ACTIONS.MOVE_TOP]: moveTop,
    [ACTIONS.GET_ANNOTATION]: getAnnotation,
    [ACTIONS.GET_ANNOTATION_MAIN_TYPE]: getAnnotationMainTypeById,
    getAnnotationRef,
    [ACTIONS.GET_ALL_ANNOTATIONS_BY_AUTHOR_ID]: getAllAnnotationsByAuthorId,
    [ACTIONS.GET_ALL_ANNOTATIONS_BY_ANNOTATION_CLASS_ID]: getAllAnnotationsByAnnotationClassId,
    [ACTIONS.CREATE_ANNOTATION]: createAnnotation,
    [ACTIONS.DUPLICATE_ANNOTATION]: duplicateAnnotation,
    [ACTIONS.UPDATE_ANNOTATION]: updateAnnotation,
    [ACTIONS.DELETE_ANNOTATION]: deleteAnnotation,
    [ACTIONS.DELETE_ANNOTATIONS]: deleteAnnotations,
    [ACTIONS.CLEAR_ANNOTATIONS]: clearAnnotations,
    [ACTIONS.SET_ANNOTATION]: setAnnotation,
    [ACTIONS.SET_ANNOTATIONS]: setAnnotations,
    [ACTIONS.LOAD_ANNOTATIONS]: loadAnnotations,
    [ACTIONS.COPY_ANNOTATIONS]: copyAnnotations,
    [ACTIONS.LOAD_ANNOTATION_SNAPSHOT]: loadAnnotationsSnapshot,
    [ACTIONS.GET_INSTANCE_ID]: getInstanceId,
    [ACTIONS.UPDATE_VIDEO_RASTER_LAYER_ANNOTATION]: updateVideoRasterLayerAnnotation,
    error,
    unsavedAnnotationCount,
    hasUnsavedAnnotation,
    unsavedAnnotationRequests,
    snapshotIdForAnnotationCopy,
    setSnapshotIdForAnnotationCopy,
  }
})
